import { Combobox, Transition } from '@headlessui/react';
import { SearchOutlined } from '@material-ui/icons';
import { observer } from 'mobx-react-lite';
import React, { useState, useMemo, Fragment, useRef, useEffect } from 'react';
import { useCallback } from 'react';
import { Input, InputGroup, InputGroupAddon, InputGroupText } from '../tempImports/reactstrap';
import { useStore } from '../../store/Store';
import cx from 'classnames';
import parsePhoneNumber from 'libphonenumber-js';

import SystemLabel from '../Systemlabel/SystemLabel';
import EventLabel from '../EventLabel/EventLabel';

function PhoneNumberField(props) {
  const inputRef = useRef(null);
  const [countryCode, setCountryCode] = useState('');
  const [prefix, setPrefix] = useState('');
  const [countryCodeSelectOpen, setCountryCodeSelectOpen] = useState(false);
  const { settings, valid, invoice, ...inputProps } = props;
  const { countries, environment } = useStore();

  useEffect(() => {
    if (!countryCode && !inputProps.value) {
      if (environment.defaultCountry) {
        const country = countries.find(c => c.countryId === environment.defaultCountry);
        if (country) {
          setCountryCode(country.alpha2Code);
          setPrefix(country.phonePrefix);
        }
      } else {
        setCountryCode('se');
        setPrefix('46');
      }
    } else {
      const currentPrefix = inputProps.value?.split(' ')[0].replace('+', '');
      const isNANP = currentPrefix === '1';
      const phoneNumber = parsePhoneNumber(inputProps.value);
      const regionCode = phoneNumber?.country;

      if (currentPrefix === prefix && !regionCode && isNANP) return;

      let country = countries.find(c => c.phonePrefix === currentPrefix);

      if (isNANP && (regionCode === 'US' || regionCode === 'CA')) {
        country = countries.find(c => c.alpha2Code === regionCode);
      }

      if (country && currentPrefix !== '') {
        setCountryCode(country.alpha2Code);
        setPrefix(country.phonePrefix);
      }
    }
  }, [environment.defaultCountry, countries, inputProps]);

  const handleChange = useCallback(
    e => {
      if (settings?.showCountryCodeSelector) {
        if (!e.target.value?.startsWith(`+${prefix}`) && inputProps?.value) {
          const newValue = e.target.value?.split(' ');
          return inputProps.onChange(`+${prefix} ${newValue?.[1] ?? ''}`);
        }
        const newValue = e.target.value?.replace(`+${prefix}`, '').replace(/[^0-9_ +-]/g, '');
        inputProps.onChange(`+${prefix} ${newValue.trimStart()}`);
      } else if (invoice) {
        inputProps.onChange(e.target.value);
      } else {
        const newValue = e.target.value?.replace(/[^0-9_ +-]/g, '');
        inputProps.onChange(newValue);
      }
    },
    [prefix, inputProps, settings?.showCountryCodeSelector]
  );

  const handleCodeChange = useCallback(
    ({ value, code }) => {
      inputProps.onChange(inputProps.value.replace(/\+[^\s]+/g, `+${value}`));
      setPrefix(value);
      setCountryCode(code);
      setCountryCodeSelectOpen(false);
    },
    [inputProps]
  );

  const inputStyle = inputRef.current ? window.getComputedStyle(inputRef.current) : {};

  if (settings?.showCountryCodeSelector) {
    return (
      <div style={{ position: 'relative', width: inputStyle?.width, marginLeft: inputStyle?.marginLeft }}>
        <input ref={inputRef} hidden />
        <InputGroup style={{ display: 'flex', flexWrap: 'nowrap', width: '100%' }}>
          <InputGroupAddon
            addonType="append"
            className="input-addon border border-r-0 pr-2"
            style={{ borderColor: inputStyle?.borderColor, borderBottomLeftRadius: 4, borderTopLeftRadius: 4, overflow: 'hidden' }}
          >
            <InputGroupText
              style={{
                display: 'flex',
                borderRadius: 0,
                borderTopLeftRadius: 5,
                borderBottomLeftRadius: 5,
                backgroundColor: '#fff',
                borderRight: 0,
                padding: '0 5px',
              }}
            >
              <button
                style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                onClick={() => setCountryCodeSelectOpen(prev => !prev)}
              >
                <img src={`https://flagcdn.com/${countryCode?.toLowerCase()}.svg`} style={{ margin: '0 5px', maxWidth: 18, width: 18, height: 'auto' }} />
              </button>
            </InputGroupText>
          </InputGroupAddon>
          {inputProps?.v}
          <Input
            {...inputProps}
            value={inputProps?.value === '' ? `+${prefix} ` : inputProps?.value}
            onChange={handleChange}
            style={{
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              marginLeft: 0,
              width: 'calc(100% + 16px + 36px)',
              border: valid === false ? '1px solid #d53443' : undefined,
            }}
          />
        </InputGroup>
        <CountryCodeSelector open={countryCodeSelectOpen} onChange={handleCodeChange} countryCode={countryCode} />
      </div>
    );
  }

  if (invoice) {
    const styleSheet = [...document.querySelector('#THISTOO')?.sheet?.rules];

    const cmsFieldTemplateWidth = styleSheet?.find(
      rule =>
        rule.selectorText ===
          `.cms-field input, .cms-field textarea, .cms-field .selector-menu, .cms-question input, .cms-question textarea, .cms-question .selector-menu` ||
        rule.selectorText === '.cms-field input, .cms-field textarea, .cms-field .select-wrap'
    )?.style?.width;

    const cmsInvoiceWidth = styleSheet?.find(
      rule => rule.selectorText === `#${inputProps?.cssId} input, #${inputProps?.cssId} textarea, #${inputProps?.cssId} .select-wrap`
    )?.style?.width;

    return (
      <div style={{ width: cmsInvoiceWidth ?? cmsFieldTemplateWidth ?? '100%' }}>
        <Input
          className="block w-full"
          innerRef={inputRef}
          style={{ border: valid === false ? '1px solid #d53443' : undefined, marginTop: valid === false && invoice ? 15 : undefined, width: '100%' }}
          {...inputProps}
          onChange={handleChange}
        />
        {valid === false && invoice && (
          <span className="block mt-1 text-sm text-danger" style={{ fontWeight: 500 }}>
            <EventLabel name="RefNoWarning" groupName={'CMS.Payment'} />
          </span>
        )}
      </div>
    );
  }

  return <Input innerRef={inputRef} style={{ border: valid === false ? '1px solid #d53443' : undefined }} {...inputProps} onChange={handleChange} />;
}

export default observer(PhoneNumberField);

const includes = (source, target) => source?.toLowerCase()?.includes(target.toLowerCase());

const CountryCodeSelector = ({ open, code, onChange }) => {
  const [query, setQuery] = useState('');
  const { countries } = useStore();

  const countryCodeList = useMemo(
    () => countries?.map(({ alpha2Code, nameInt, phonePrefix }) => ({ code: alpha2Code, label: nameInt, value: phonePrefix })),
    [countries]
  );

  const filteredCountries = useMemo(
    () => countryCodeList.filter(({ label, value }) => includes(label, query) || includes(`+${value}`, query)).sort((a, b) => a.label.localeCompare(b.label)),
    [countryCodeList, query]
  );

  return (
    open && (
      <div
        className="select-wrap selector intended"
        style={{ position: 'absolute', zIndex: 40, transform: 'translate(0, 10px)', background: '#fff', width: '100%' }}
      >
        <Combobox value={null} onChange={country => onChange(country)}>
          <div className="autocomplete" style={{ height: '100%' }}>
            <div
              className="input-base"
              style={{ display: 'flex', backgroundColor: '#fff', alignItems: 'center', padding: '3px 10px', border: '1px solid gainsboro', width: '100%' }}
            >
              <SearchOutlined style={{ color: '#8b8888' }} />
              <Combobox.Input onChange={event => setQuery(event.target.value)} style={{ border: 0, background: '#fff', width: '100%' }} />
            </div>
            <Transition show as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0" afterLeave={() => setQuery('')}>
              <Combobox.Options style={{ listStyle: 'none', background: '#fff', padding: '5px 0', overflowY: 'auto', maxHeight: 300 }}>
                {filteredCountries?.length === 0 ? (
                  <div style={{ padding: 10 }}>
                    <SystemLabel name="NoResult" />
                  </div>
                ) : (
                  filteredCountries
                    ?.sort((a, b) => (a.label > b.label ? 1 : a.label < b.label ? -1 : 0))
                    .map(({ code, value, label }) => (
                      <Combobox.Option key={code} value={{ value, code }} as={Fragment} style={{ padding: '5px 10px', cursor: 'pointer' }}>
                        {({ selected }) => (
                          <li className={cx('flex cursor-pointer gap-x-2 items-center', selected ? 'selected' : '')}>
                            <div className="w-4">
                              <img src={`https://flagcdn.com/${code?.toLowerCase()}.svg`} className="max-w-3 w-auto h-auto max-h-3 bg-slate-50" />
                            </div>
                            <span>
                              {label} <code className="bg-slate-100 text-xs p-1 rounded-md">+{value}</code>
                            </span>
                          </li>
                        )}
                      </Combobox.Option>
                    ))
                )}
              </Combobox.Options>
            </Transition>
          </div>
        </Combobox>
      </div>
    )
  );
};
